import React from 'react';
import { CartesianGrid, Label, Line, LineChart, Pie, PieChart, XAxis } from 'recharts';

import { Trend, TrendValue } from '../trend-value';

import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { formatNumber } from '@/lib/formatNumber';
import { useChartDateFormatters } from '@/lib/useChartDateFormatters';
import { cn } from '@/lib/utils';

export interface ArticlesCoverageProps {
  total: number;
  trend: Trend;
  trendValue: string;
  coveragePercentage: number;
  data: Array<Record<string, number | string | Date>>;
  chartConfig: ChartConfig;
  locale?: string;
  className?: string;
}

const formatWhole = (value: number) => formatNumber(value, {});

const COVERAGE_CHART_CONFIG = {
  covered: {
    label: 'Covered',
    color: 'var(--bfc-v2-color-blue-600, #357DE8)',
  },
  uncovered: {
    label: 'Uncovered',
    color: 'var(--bfc-v2-color-mono-300, #E5E5E5)',
  },
} satisfies ChartConfig;

export function ArticlesCoverage({
  total,
  trend,
  trendValue,
  coveragePercentage,
  data,
  chartConfig,
  locale,
  className,
}: ArticlesCoverageProps) {
  const { tickFormatter, formatXLabel } = useChartDateFormatters(locale);
  const lineKeys = Object.keys(chartConfig);
  const clampedCoverage = Math.round(Math.min(100, Math.max(0, coveragePercentage)));

  return (
    <Card className={cn('shadow-none border border-dark-300 rounded-lg', className)}>
      <CardHeader className="p-0 space-y-0 border-b border-dark-300">
        <div className="flex flex-col gap-1 justify-center py-4 px-4">
          <p className="uppercase heading-xxxs font-semibold text-dark tracking-normal">
            Articles Generated by Brainfish (coverage)
          </p>
          <div className="flex items-center gap-2">
            <span className="font-mono text-3xl leading-10 text-dark">{formatWhole(total)}</span>
            <TrendValue trend={trend} trendValue={trendValue} />
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex gap-2 p-0">
        <div className="flex-1 min-w-0 px-4 pt-4 pb-0">
          <ChartContainer config={chartConfig} className="h-48 w-full">
            <LineChart data={data} margin={{ top: 8, left: 20, right: 20, bottom: 0 }}>
              <CartesianGrid horizontal vertical={false} className="stroke-dark-300" />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                padding={{ left: 15, right: 15 }}
                minTickGap={32}
                interval="preserveStartEnd"
                tickFormatter={tickFormatter}
                tick={({ x, y, payload, index }) => {
                  const isFirst = index === 0;
                  const isLast = index === data.length - 1;
                  const anchor = isFirst ? 'start' : isLast ? 'end' : 'middle';
                  const tickX = isFirst ? x - 15 : isLast ? x + 15 : x;

                  return (
                    <text x={tickX} y={y} dy={12} textAnchor={anchor} className="text-xs fill-dark-600">
                      {tickFormatter(payload.value)}
                    </text>
                  );
                }}
              />
              <ChartTooltip
                cursor={false}
                content={({ active, payload, label }) => (
                  <ChartTooltipContent
                    active={active}
                    payload={payload}
                    label={label != null ? formatXLabel(label) : ''}
                  />
                )}
              />
              {lineKeys.map((key) => (
                <Line
                  key={key}
                  dataKey={key}
                  type="monotone"
                  stroke={`var(--color-${key})`}
                  strokeWidth={2}
                  dot={{ r: 4, fill: 'white', strokeWidth: 2, stroke: `var(--color-${key})` }}
                  activeDot={{ r: 5, fill: 'white', strokeWidth: 2, stroke: `var(--color-${key})` }}
                />
              ))}
            </LineChart>
          </ChartContainer>
        </div>

        <div className="w-80 shrink-0 flex items-center justify-center">
          <ChartContainer config={COVERAGE_CHART_CONFIG} className="aspect-square w-full max-w-44">
            <PieChart>
              <Pie
                data={[{ value: 100, fill: 'var(--color-uncovered)' }]}
                dataKey="value"
                innerRadius="76%"
                outerRadius="82%"
                stroke="none"
                isAnimationActive={false}
              />
              <Pie
                data={[
                  { name: 'covered', value: clampedCoverage, fill: 'var(--color-covered)' },
                  { name: 'uncovered', value: 100 - clampedCoverage, fill: 'transparent' },
                ]}
                dataKey="value"
                nameKey="name"
                innerRadius="68%"
                outerRadius="90%"
                startAngle={90}
                endAngle={-270}
                stroke="none"
              >
                <Label
                  content={({ viewBox }) => {
                    if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                      return (
                        <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="central">
                          <tspan className="font-mono text-3xl fill-dark-600">{clampedCoverage}%</tspan>
                        </text>
                      );
                    }

                    return null;
                  }}
                />
              </Pie>
            </PieChart>
          </ChartContainer>
        </div>
      </CardContent>
    </Card>
  );
}
